import type { HTMLChakraProps } from '@chakra-ui/react';
import { Flex } from '@chakra-ui/react';
import React from 'react';

import type { PaginationParams } from './types';

import { Button } from 'toolkit/chakra/button';
import { IconButton } from 'toolkit/chakra/icon-button';
import { Input } from 'toolkit/chakra/input';
import { Skeleton } from 'toolkit/chakra/skeleton';
import IconSvg from 'ui/shared/IconSvg';

interface Props extends PaginationParams, Omit<HTMLChakraProps<'div'>, 'page' | 'direction'> {
  readonly totalPages?: number;
}

const Pagination = (props: Props) => {
  const {
    page,
    onNextPageClick,
    onPrevPageClick,
    resetPage,
    hasPages,
    hasNextPage,
    canGoBackwards,
    isLoading,
    isVisible,
    totalPages,
    onPageChange,
    ...rest
  } = props;

  const [ pageInput, setPageInput ] = React.useState(String(page));

  React.useEffect(() => {
    setPageInput(String(page));
  }, [ page ]);

  const requestedPage = Number(pageInput);
  const isValidPage = pageInput.trim() !== '' && Number.isSafeInteger(requestedPage) && requestedPage >= 1 &&
    (totalPages === undefined || requestedPage <= totalPages);

  const handlePageSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!isLoading && isValidPage && requestedPage !== page) {
      onPageChange?.(requestedPage);
    }
  };

  if (!isVisible) {
    return null;
  }

  const showSkeleton = page === 1 && !hasPages && isLoading;

  return (
    <Flex
      as="nav"
      alignItems="center"
      flexWrap="nowrap"
      { ...rest }
    >
      <Skeleton loading={ showSkeleton } mr={ 3 }>
        <Button
          variant="pagination"
          size="sm"
          onClick={ resetPage }
          disabled={ page === 1 || isLoading }
        >
          First
        </Button>
      </Skeleton>
      <IconButton
        aria-label="Prev page"
        variant="pagination"
        boxSize={ 8 }
        onClick={ onPrevPageClick }
        disabled={ !canGoBackwards || isLoading || page === 1 }
        loadingSkeleton={ showSkeleton }
      >
        <IconSvg name="arrows/east-mini" boxSize={ 5 }/>
      </IconButton>
      <Button
        variant="pagination"
        size="sm"
        selected={ !showSkeleton }
        pointerEvents="none"
        loadingSkeleton={ showSkeleton }
        mx={ 2 }
        minW={ 8 }
        px={ 2 }
      >
        { totalPages !== undefined ? `Page ${page} of ${totalPages}` : page }
      </Button>
      <IconButton
        aria-label="Next page"
        variant="pagination"
        boxSize={ 8 }
        onClick={ onNextPageClick }
        disabled={ !hasNextPage || isLoading }
        loadingSkeleton={ showSkeleton }
      >
        <IconSvg name="arrows/east-mini" boxSize={ 5 } transform="rotate(180deg)"/>
      </IconButton>
      { onPageChange && (
        <Flex as="form" onSubmit={ handlePageSubmit } alignItems="center" gap={ 1 } ml={ 2 } flexShrink={ 0 }>
          <Input
            aria-label="Page number"
            type="number"
            inputMode="numeric"
            min={ 1 }
            max={ totalPages }
            step={ 1 }
            required
            size="sm"
            w="5rem"
            flexShrink={ 0 }
            value={ pageInput }
            onChange={ (event) => setPageInput(event.target.value) }
            disabled={ isLoading }
          />
          <Button type="submit" variant="pagination" size="sm" disabled={ isLoading || !isValidPage || requestedPage === page }>
            Go
          </Button>
        </Flex>
      ) }
    </Flex>

  );
};

export default React.memo(Pagination);
